import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import ElementHeightObserver from 'components/ElementHeightObserver';
import MobileAccountBackBtn from 'components/MobileAccountBackBtn';
import MobilePreferences from 'components/MobilePreferences/MobilePreferences';
import PreferencesDropdownView from 'components/PreferencesDropdownView/PreferencesDropdownView';
import { mobileAccountPagesBranding } from 'constants/branding';
import { oddsTypePreferencesOptions } from 'constants/oddsTypes';
import useMobilePreferences from 'hooks/useMobilePreferences';
import {
  getIsExchangeGamesEnabled,
  getIsExchangeSportsEnabled,
  getIsIframeEnabled,
  getMobileSettingsDefStake,
  getMobileSettingsDepositBtn,
  getMobileSettingsQuickstakeBetslip
} from 'redux/modules/appConfigs/selectors';
import { getAppSettings } from 'redux/modules/appSettings/selectors';
import { MobilePreferencesOption } from 'types/mobile';
import { getDefaultLinkTarget } from 'utils/navigation';

import AccountLinksSection from './components/AccountLinksSection/AccountLinksSection';
import BetSlipSettingsSection from './components/BetSlipSettingsSection/BetSlipSettingsSection';
import DefaultStakeSection from './components/DefaultStakeSection/DefaultStakeSection';
import QuickBetsSection from './components/QuickBetsSection/QuickBetsSection';
import QuickBetsView from './components/QuickBetsView/QuickBetsView';

import styles from './styles.module.scss';

const MobileAccount = () => {
  const { t } = useTranslation();

  const depositBtn = useSelector(getMobileSettingsDepositBtn);
  const settings = useSelector(getAppSettings);
  const isIframeEnabled = useSelector(getIsIframeEnabled);
  const defStake = useSelector(getMobileSettingsDefStake);
  const quickstakeBetslip = useSelector(getMobileSettingsQuickstakeBetslip);
  const exchangeSportsEnabled = useSelector(getIsExchangeSportsEnabled);
  const exchangeGamesEnabled = useSelector(getIsExchangeGamesEnabled);

  const [isQuickBetsView, setIsQuickBetsView] = useState(false);
  const [headerResizeElement, setHeaderResizeElement] = useState<HTMLElement | null>(null);

  const isShowDepositLink = depositBtn && settings.length;
  const isQuickStakesEnabled = quickstakeBetslip && (exchangeSportsEnabled || exchangeGamesEnabled);

  const depositLinkTarget = getDefaultLinkTarget({
    url: settings[0]?.url,
    absolutePath: true,
    isIframeEnabled,
    currentTarget: '_blank',
    openInNewTab: true
  });

  const {
    setShowTimezoneView,
    setShowLocaleView,
    setShowOddsFormatView,
    selectedLocale,
    selectedOffset,
    selectedOddsFormatOption,
    showLocaleView,
    showTimezoneView,
    showOddsFormatView,
    onSelectLocal,
    onSelectTimezone,
    onSelectOddsType,
    timezoneOffsets,
    locales
  } = useMobilePreferences();

  useEffect(() => {
    setHeaderResizeElement(document.getElementById('header-resize-container'));
  }, []);

  if (isQuickBetsView) {
    return (
      <ElementHeightObserver>
        <QuickBetsView onGoBack={() => setIsQuickBetsView(false)} />
      </ElementHeightObserver>
    );
  }

  if (showLocaleView && selectedLocale) {
    return (
      <PreferencesDropdownView
        onGoBack={() => setShowLocaleView(false)}
        onChangeOption={onSelectLocal}
        label={t('locale.label')}
        options={locales}
        selectedOption={selectedLocale}
        headerResizeElement={headerResizeElement}
        formatLabel={label => label.toLowerCase()}
      />
    );
  }

  if (showTimezoneView && selectedOffset) {
    return (
      <PreferencesDropdownView
        onGoBack={() => setShowTimezoneView(false)}
        onChangeOption={onSelectTimezone}
        label={t('timezone.label')}
        options={timezoneOffsets as MobilePreferencesOption[]}
        selectedOption={selectedOffset as MobilePreferencesOption}
        headerResizeElement={headerResizeElement}
      />
    );
  }

  if (showOddsFormatView) {
    return (
      <PreferencesDropdownView
        onGoBack={() => setShowOddsFormatView(false)}
        onChangeOption={onSelectOddsType}
        label={t('account.settings.oddsFormat')}
        options={oddsTypePreferencesOptions}
        selectedOption={selectedOddsFormatOption}
        headerResizeElement={headerResizeElement}
        formatLabel={label => t(label)}
      />
    );
  }

  const header = (
    <div className={classNames(styles.account__header, mobileAccountPagesBranding.SUBHEADER)}>
      <MobileAccountBackBtn
        className={styles.account__header__home}
        hideLabel
        iconName="biab_custom-icon-home"
        iconClassName={styles.account__header__home__icon}
      />
      <h2 className={styles.account__header__title}>{t('header.myAccount')}</h2>
      {isShowDepositLink ? (
        <a href={settings[0]?.url} target={depositLinkTarget} className={styles.account__header__depositLink}>
          {t('accountNavigation.deposit')}
        </a>
      ) : (
        <div className={styles.account__header__empty} />
      )}
    </div>
  );

  return (
    <ElementHeightObserver>
      {headerResizeElement ? createPortal(header, headerResizeElement) : header}
      <div className={styles.account__container}>
        <div className={classNames(styles.account__container__topBg, mobileAccountPagesBranding.SUBHEADER)} />
        <div className={styles.account__content}>
          <AccountLinksSection />
          <BetSlipSettingsSection />
          {defStake && <DefaultStakeSection />}
          {isQuickStakesEnabled && <QuickBetsSection showQuickBetsView={() => setIsQuickBetsView(true)} />}
          <MobilePreferences
            setShowLocaleView={setShowLocaleView}
            setShowTimezoneView={setShowTimezoneView}
            setShowOddsFormatView={setShowOddsFormatView}
            selectedOffset={selectedOffset as MobilePreferencesOption}
            selectedLocale={selectedLocale}
            selectedOddsFormatOption={selectedOddsFormatOption}
            isAccountPage
          />
        </div>
      </div>
    </ElementHeightObserver>
  );
};

export default MobileAccount;
